import React, { useState } from 'react';

import CentralizedSpinner from 'components/CentralizedSpinner';
import Icon from 'components/DesignSystem/Icon';
import QueryCard from 'components/Meteorology/Shared/QueryCard';
import QueryCardHeader from 'components/Meteorology/Shared/QueryCard/Header';
import QueryCardChips from 'components/Meteorology/Shared/QueryCard/Chips';
import QueryCardChip from 'components/Meteorology/Shared/QueryCard/Chip';
import QueryCardTrigger from 'components/Meteorology/Shared/QueryCard/Trigger';
import About from 'components/Meteorology/Agro/About';
import useOptions from 'hooks/Meteorology/Agro/WaterBalanceSequential/Maps/useOptions';
import useVariables from 'hooks/Meteorology/Agro/WaterBalanceSequential/Maps/useVariables';
import usePeriod from 'hooks/Meteorology/Agro/WaterBalanceSequential/Maps/usePeriod';
import useMapLocationMarkers from 'hooks/Meteorology/Agro/WaterBalanceSequential/Maps/useMapLocationMarkers';
import useMapDownload from 'hooks/Meteorology/Agro/WaterBalanceSequential/Maps/useMapDownload';
import useAgroMapState from 'hooks/Meteorology/Agro/Maps/useAgroMapState';
import { cssVar } from 'constants/theme';

import MapDisplay from './MapDisplay';
import ControlPanelSheet from './ControlPanelSheet';
import LayersSheet from './LayersSheet';

import './index.scss';

// No mobile não há hover, então a camada "tooltip" do desktop não se aplica.
const LAYER_DEFS = [
  { key: 'localidades', label: 'Localidades' },
  { key: 'limitesEstaduais', label: 'Limites estaduais' },
];

export default function MapsMobile() {
  const [panelOpen, setPanelOpen] = useState(false);
  const [layersOpen, setLayersOpen] = useState(false);
  const [showAbout, setShowAbout] = useState(false);

  const { isLoading } = useOptions();
  const { variableLabel } = useVariables();
  const { startDate, endDate, selectsRange } = usePeriod();
  const locationMarkers = useMapLocationMarkers();
  const {
    onDownloadClick, setOnDownloadClick, downloadDisabled, mapFilename,
  } = useMapDownload();
  const { layers, handleToggle } = useAgroMapState(LAYER_DEFS);

  if (isLoading) {
    return <CentralizedSpinner />;
  }

  const locationCount = locationMarkers.length;
  const chips: string[] = [];
  if (startDate) {
    const start = startDate.toLocaleDateString('pt-BR');
    chips.push(
      selectsRange && endDate && startDate.getTime() !== endDate.getTime()
        ? `${start} – ${endDate.toLocaleDateString('pt-BR')}`
        : start,
    );
  }
  if (locationCount > 0) {
    chips.push(`${locationCount} ${locationCount === 1 ? 'localidade' : 'localidades'}`);
  }

  return (
    <div className="agro-wbs-maps-mobile">
      <QueryCard>
        <QueryCardHeader label="Variável exibida" title={variableLabel}>
          {chips.length > 0 && (
            <QueryCardChips>
              {chips.map((chip) => <QueryCardChip key={chip}>{chip}</QueryCardChip>)}
            </QueryCardChips>
          )}
        </QueryCardHeader>
        <QueryCardTrigger onClick={() => setPanelOpen(true)} hint="Variável · Período · Camadas" />
      </QueryCard>

      <div className="agro-wbs-maps-mobile-card">
        <div className="agro-wbs-maps-mobile-card-header">
          <span className="agro-wbs-maps-mobile-card-title">Distribuição no Brasil</span>
          <div className="agro-wbs-maps-mobile-card-actions">
            <button
              type="button"
              aria-label="Instruções"
              className="agro-wbs-maps-mobile-action"
              onClick={() => setShowAbout(true)}
            >
              <Icon iconType="info" color={cssVar('text-strong')} size="small" />
            </button>
            <button
              type="button"
              aria-label="Baixar mapa"
              className="agro-wbs-maps-mobile-action"
              onClick={onDownloadClick}
              disabled={downloadDisabled}
            >
              <Icon iconType="download" color={cssVar('text-strong')} size="small" />
            </button>
            <button
              type="button"
              className="agro-wbs-maps-mobile-action agro-wbs-maps-mobile-action-layers"
              onClick={() => setLayersOpen(true)}
            >
              <Icon iconType="layers" color={cssVar('text-strong')} size="small" />
              Camadas
            </button>
          </div>
        </div>

        <MapDisplay
          layers={layers}
          setOnDownloadClick={setOnDownloadClick}
          mapFilename={mapFilename}
        />
        <div className="agro-wbs-maps-mobile-hint">
          <Icon iconType="star" color={cssVar('text-accent')} size="small" />
          <span>Toque num pin para abrir a análise detalhada da localidade.</span>
        </div>
      </div>

      <ControlPanelSheet open={panelOpen} dismiss={() => setPanelOpen(false)} />
      <LayersSheet
        open={layersOpen}
        dismiss={() => setLayersOpen(false)}
        layers={layers}
        onToggle={handleToggle}
        definitions={LAYER_DEFS}
      />
      <About show={showAbout} onHide={() => setShowAbout(false)} />
    </div>
  );
}
